"use client"

import * as React from "react"
import { formatDate } from "@/shared/lib/dates/format-date"
import { useAuth } from "@clerk/clerk-react"
import { useQueryClient } from "@tanstack/react-query"
import { useLocation, useNavigate } from "react-router-dom"
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@ibada/ui"
import { toast } from "sonner"

import {
  buildAdminHref,
  buildWorkspaceSettingsHref,
  getActiveWorkspaceAccesses,
  hasWorkspaceAuthorityAccess,
  resolveDefaultBranchSlugForWorkspaceAccess,
  WORKSPACE_SETTINGS_BRANCHES_PATH,
  WORKSPACES_LIST_PATH,
} from "@/features/auth"
import { refreshAuthMeContext, useAcceptInvitationMutation, useDeclineInvitationMutation, useMarkWorkspaceInvitationsViewedMutation, useUnviewedWorkspaceInvitationQuery } from "@/features/auth/services/auth-query"
import { useAuthContextStore } from "@/features/auth/services/auth-context-store"
import { useWorkspaceInvitationAwarenessStore } from "@/features/auth/services/workspace-invitation-awareness-store"
import { type InvitationRecord } from "@/features/auth/model/auth-types"
import { useTranslations } from "@/i18n"
import { AnalyticsEvents, captureEvent } from "@/shared/lib/analytics/analytics"
import { useIsMobile } from "@/shared/hooks/use-mobile"
import { MobileFormCheckRow, MobileFormRow, MobileFormSection } from "@/shared/components/mobile-form"

const POLLING_START_DELAY_MS = 45 * 1000

/** Below this, the invitation is worth flagging rather than just dating — it is about to stop working. */
const EXPIRING_SOON_DAYS = 7

function getWorkspaceLabel(invitation: InvitationRecord) {
  return invitation.workspace?.displayName?.trim() || invitation.workspace?.name || invitation.workspace?.slug || "-"
}

/** Two letters from the workspace name, for when it has no logo. */
function getWorkspaceInitials(label: string) {
  return label
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? "")
    .join("")
}

function getDaysUntil(isoDate: string) {
  const remaining = new Date(isoDate).getTime() - Date.now()
  return Number.isNaN(remaining) ? null : Math.ceil(remaining / (24 * 60 * 60 * 1000))
}

export function WorkspaceInvitationAwarenessDialog() {
  const t = useTranslations("auth.invitationAwareness")
  const tRoles = useTranslations("users.table.roles")
  const { isLoaded, isSignedIn, userId } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const authMe = useAuthContextStore((state) => state.authMe)
  const promptsSuppressed = useWorkspaceInvitationAwarenessStore((state) => state.promptsSuppressed)
  const suppressPrompts = useWorkspaceInvitationAwarenessStore((state) => state.suppressPrompts)
  const resetPrompts = useWorkspaceInvitationAwarenessStore((state) => state.resetPrompts)
  const [pollingEnabled, setPollingEnabled] = React.useState(false)
  const [invitation, setInvitation] = React.useState<InvitationRecord | null>(null)
  const [switchWorkspace, setSwitchWorkspace] = React.useState(false)
  const isMobile = useIsMobile()
  const lastUserIdRef = React.useRef<string | null>(null)
  const isInvitationReviewPage = location.pathname === "/accept-invitation"
  const hasFallbackWorkspace = Boolean(authMe && getActiveWorkspaceAccesses(authMe).length > 0)

  const awarenessQuery = useUnviewedWorkspaceInvitationQuery({
    enabled: pollingEnabled && !promptsSuppressed && !isInvitationReviewPage,
  })
  const { mutate: markInvitationViewed } = useMarkWorkspaceInvitationsViewedMutation()
  const acceptMutation = useAcceptInvitationMutation()
  const declineMutation = useDeclineInvitationMutation()

  React.useEffect(() => {
    if (!isLoaded || !isSignedIn || !userId) {
      setPollingEnabled(false)
      setInvitation(null)
      lastUserIdRef.current = null
      resetPrompts()
      return
    }

    if (lastUserIdRef.current !== userId) {
      lastUserIdRef.current = userId
      resetPrompts()
    }

    const timeoutId = window.setTimeout(() => setPollingEnabled(true), POLLING_START_DELAY_MS)
    return () => window.clearTimeout(timeoutId)
  }, [isLoaded, isSignedIn, resetPrompts, userId])

  React.useEffect(() => {
    if (!awarenessQuery.data || invitation || promptsSuppressed || isInvitationReviewPage) return
    setSwitchWorkspace(false)
    setInvitation(awarenessQuery.data)
    markInvitationViewed({ invitationIds: [awarenessQuery.data.id] })
  }, [awarenessQuery.data, invitation, isInvitationReviewPage, markInvitationViewed, promptsSuppressed])

  const navigateToAcceptedWorkspace = React.useCallback(
    (acceptedWorkspaceId: string, refreshedAuthMe: NonNullable<typeof authMe>) => {
      const access = refreshedAuthMe.workspaceAccess.find((entry) => entry.workspace.id === acceptedWorkspaceId)
      if (!access) {
        navigate(WORKSPACES_LIST_PATH)
        return
      }

      const branchSlug = resolveDefaultBranchSlugForWorkspaceAccess(access)
      if (branchSlug) {
        navigate(buildAdminHref(access.workspace.slug, branchSlug), { replace: true })
        return
      }

      // AUTHORIZATION_POLICY_EXCEPTION: Invitation acceptance occurs before the freshly accepted
      // workspace's tenant-authorization snapshot is available. This is only a post-acceptance landing
      // selection (not feature visibility or a mutation grant); the next mounted route re-evaluates access
      // through the central registry.
      if (hasWorkspaceAuthorityAccess(access)) {
        navigate(buildWorkspaceSettingsHref(access.workspace.slug, WORKSPACE_SETTINGS_BRANCHES_PATH), { replace: true })
        return
      }

      navigate(WORKSPACES_LIST_PATH, { replace: true })
    },
    [navigate],
  )

  if (!invitation?.workspaceId) return null

  const invitationWorkspaceId = invitation.workspaceId
  const busy = acceptMutation.isPending || declineMutation.isPending
  const workspaceName = getWorkspaceLabel(invitation)
  const daysLeft = getDaysUntil(invitation.expiresAt)
  /*
   * The role rendered as the raw database constant — "USER" — at a French reader. These labels already
   * exist for the users table; this points at them, and falls back to the constant rather than nothing
   * if a new role key ever ships ahead of its translation.
   */
  const roleLabel = invitation.roleKey ? tRoles(invitation.roleKey) : null

  const later = () => {
    suppressPrompts()
    setInvitation(null)
  }
  const decline = async () => {
    try {
      await declineMutation.mutateAsync({ code: invitation.code, workspaceId: invitationWorkspaceId })
      setInvitation(null)
    } catch {
      toast.error(t("declineFailed"))
    }
  }
  const accept = async () => {
    try {
      await acceptMutation.mutateAsync({ code: invitation.code, workspaceId: invitationWorkspaceId })
      captureEvent(AnalyticsEvents.InvitationAccepted, {
        workspace_id: invitationWorkspaceId,
        invitation_id: invitation.id,
      })
      const refreshedAuthMe = await refreshAuthMeContext(queryClient)
      setInvitation(null)
      if (switchWorkspace || !hasFallbackWorkspace) {
        navigateToAcceptedWorkspace(invitationWorkspaceId, refreshedAuthMe)
      }
    } catch {
      toast.error(t("acceptFailed"))
    }
  }
  const expiringSoon = daysLeft !== null && daysLeft <= EXPIRING_SOON_DAYS

  if (isMobile) {
    // Phone: the app's bottom sheet, still not dismissible by a swipe or a tap behind it (only "Plus tard" is a
    // way out). The church as the subject (its logo and name, the role), the deadline as a row, then Accepter,
    // Refuser and Plus tard stacked for the thumb.
    return (
      <Drawer open dismissible={false}>
        <DrawerContent className="max-h-[92dvh]">
          <DrawerHeader className="items-center gap-3 pt-5 text-center group-data-[vaul-drawer-direction=bottom]/drawer-content:text-center">
            <Avatar className="size-16 rounded-2xl">
              <AvatarImage src={invitation.workspace?.logoUrl ?? undefined} alt="" className="rounded-2xl" />
              <AvatarFallback className="bg-primary/10 text-primary rounded-2xl text-xl font-extrabold">
                {getWorkspaceInitials(workspaceName)}
              </AvatarFallback>
            </Avatar>
            <DrawerTitle className="text-xl font-extrabold">{t("title")}</DrawerTitle>
            <DrawerDescription className="text-[15px] font-medium">{t("description")}</DrawerDescription>
          </DrawerHeader>
          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto [&>*]:shrink-0 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] [&_section>div]:bg-muted/60">
            <MobileFormSection>
              <MobileFormRow label={workspaceName} value={roleLabel ?? undefined} />
              <MobileFormRow
                label={t("expires")}
                value={formatDate(invitation.expiresAt, { variant: "datetime" })}
                sublabel={
                  expiringSoon ? (
                    <span className="text-amber-600 dark:text-amber-500">{t("expiresSoon", { days: Math.max(daysLeft ?? 0, 0) })}</span>
                  ) : undefined
                }
              />
            </MobileFormSection>
            {hasFallbackWorkspace ? (
              <MobileFormSection>
                <MobileFormCheckRow
                  label={t("switchAfterAccept")}
                  checked={switchWorkspace}
                  onToggle={() => setSwitchWorkspace((value) => !value)}
                  disabled={busy}
                />
              </MobileFormSection>
            ) : null}
            <div className="flex flex-col gap-2.5">
              <button
                type="button"
                onClick={() => void accept()}
                disabled={busy}
                className="flex h-[3.25rem] items-center justify-center rounded-full bg-primary text-base font-extrabold text-primary-foreground active:brightness-95 disabled:opacity-45"
              >
                {acceptMutation.isPending ? (
                  <span className="size-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                ) : (
                  t("accept")
                )}
              </button>
              <button
                type="button"
                onClick={() => void decline()}
                disabled={busy}
                className="flex h-[3.25rem] items-center justify-center rounded-full bg-muted text-base font-extrabold text-foreground active:brightness-95 disabled:opacity-60"
              >
                {declineMutation.isPending ? (
                  <span className="size-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                ) : (
                  t("decline")
                )}
              </button>
              {hasFallbackWorkspace ? (
                <button
                  type="button"
                  onClick={later}
                  disabled={busy}
                  className="h-11 rounded-full text-base font-bold text-primary active:bg-primary/10 disabled:opacity-60"
                >
                  {t("later")}
                </button>
              ) : null}
            </div>
          </div>
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Dialog open onOpenChange={() => undefined}>
      {/*
        Not dismissible by accident. Answering an invitation is a real decision with a deadline, and a
        stray click on the page behind it is not an answer — "Plus tard" is, and it says so.
      */}
      <DialogContent
        showCloseButton={false}
        size="lg"
        onInteractOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{t("title")}</DialogTitle>
          <DialogDescription>{t("description")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 px-4 py-4 text-sm sm:px-6">
          {/*
            The workspace is the whole subject of this decision, so it gets the identity treatment rather
            than a "Espace: …" row that repeated what the title already said.
          */}
          <div className="border-border/60 bg-muted/30 flex items-center gap-3 rounded-lg border p-3">
            <Avatar className="size-10 rounded-lg">
              <AvatarImage src={invitation.workspace?.logoUrl ?? undefined} alt="" className="rounded-lg" />
              <AvatarFallback className="bg-primary/10 text-primary rounded-lg text-sm font-semibold">
                {getWorkspaceInitials(workspaceName)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="text-foreground truncate font-semibold">{workspaceName}</p>
              {invitation.workspace?.slug ? (
                <p className="text-muted-foreground truncate text-xs">{invitation.workspace.slug}</p>
              ) : null}
            </div>
            {roleLabel ? <Badge variant="secondary">{roleLabel}</Badge> : null}
          </div>

          <dl className="border-border/60 divide-border/60 divide-y rounded-lg border">
            <div className="flex items-center justify-between gap-3 px-3 py-2">
              <dt className="text-muted-foreground">{t("expires")}</dt>
              <dd className="flex items-center gap-2">
                {/* A date alone does not say "soon". Three days out is a different decision from three months. */}
                {daysLeft !== null && daysLeft <= EXPIRING_SOON_DAYS ? (
                  <Badge variant="warning">{t("expiresSoon", { days: Math.max(daysLeft, 0) })}</Badge>
                ) : null}
                <span>{formatDate(invitation.expiresAt, { variant: "datetime" })}</span>
              </dd>
            </div>
          </dl>

          {hasFallbackWorkspace ? (
            <label className="border-border/60 hover:bg-muted/40 flex cursor-pointer items-start gap-3 rounded-lg border p-3">
              <Checkbox checked={switchWorkspace} onCheckedChange={(checked) => setSwitchWorkspace(checked === true)} />
              <span>{t("switchAfterAccept")}</span>
            </label>
          ) : null}
        </div>

        <DialogFooter>
          {hasFallbackWorkspace ? (
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={later}
            >
              {t("later")}
            </Button>
          ) : null}
          <Button
            type="button"
            variant="outline"
            loading={declineMutation.isPending}
            disabled={busy}
            onClick={() => void decline()}
          >
            {t("decline")}
          </Button>
          <Button
            type="button"
            loading={acceptMutation.isPending}
            disabled={busy}
            onClick={() => void accept()}
          >
            {t("accept")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
